import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaSave } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import useColleges from '../../../../utils/useColleges';
import AttendanceNav from '../AttendanceNav';
import '../AttendanceNav.css';
import './Attendance.css';

const Attendance = () => {
  const { user }  = useAuth();
  const isAdmin   = user?.role === 'admin' || (user?.colleges?.length > 0);
  const { colleges, loading: collegesLoading } = useColleges();

  const [selectedCollege, setSelectedCollege] = useState('');
  const [departments, setDepartments]         = useState([]);
  const [employees, setEmployees]             = useState([]);
  const [holidays, setHolidays]               = useState([]);
  const [monthYear, setMonthYear] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedDept, setSelectedDept]       = useState('');
  const [deptSearchInput, setDeptSearchInput] = useState('All Departments');
  const [showDeptDrop, setShowDeptDrop]       = useState(false);
  const [expandedRow, setExpandedRow]         = useState(null);
  const [saving, setSaving]                   = useState(false);
  
  // Local pending attendance: { [empId]: { present: number, marked: bool } }
  const [attendance, setAttendance]           = useState({});
  // Already saved DB records: { [empId]: recordObject }
  const [savedRecords, setSavedRecords]       = useState({});

  const deptDropRef = useRef(null);

  useEffect(() => {
    if (!isAdmin && colleges.length > 0) setSelectedCollege(String(colleges[0]._id));
  }, [isAdmin, colleges]);

  useEffect(() => {
    if (isAdmin && colleges.length === 1) setSelectedCollege(String(colleges[0]._id));
  }, [isAdmin, colleges]);

  useEffect(() => {
    if (!selectedCollege) { setDepartments([]); setEmployees([]); setHolidays([]); return; }
    api.get('/departments', { params: { college: selectedCollege } })
      .then(r => setDepartments(r.data.data || [])).catch(() => {});
    api.get('/employees', { params: { limit: 500 } })
      .then(r => setEmployees((r.data.data || []).filter(e =>
        String(e.college?._id || e.college) === String(selectedCollege) && e.isActive !== false
      ))).catch(() => {});
  }, [selectedCollege]);

  // Fetch holidays and saved attendance records for the selected month/year
  const fetchMonthData = () => {
    if (!selectedCollege || !monthYear) { setHolidays([]); setSavedRecords({}); return; }
    const [year, month] = monthYear.split('-');
    
    // Fetch Holidays
    api.get('/holidays').then(r => {
      setHolidays((r.data.data || []).filter(h => {
        const hDate = new Date(h.date);
        return String(h.college?._id || h.college) === String(selectedCollege) &&
          hDate.getFullYear() === Number(year) &&
          hDate.getMonth() + 1 === Number(month);
      }));
    }).catch(() => {});

    // Fetch saved attendance to lock inputs
    api.get('/attendance', { params: { college: selectedCollege, month, year } })
      .then(r => {
        const map = {};
        (r.data.data || []).forEach(rec => {
          const empId = rec.employee?._id || rec.employee;
          map[empId] = rec;
        });
        setSavedRecords(map);
      }).catch(() => {});
  };

  useEffect(() => {
    fetchMonthData();
    // Clear local pending edits when month/college changes
    setAttendance({});
  }, [selectedCollege, monthYear]);

  const daysInMonth = useMemo(() => {
    if (!monthYear) return 30;
    const [year, month] = monthYear.split('-');
    return new Date(year, month, 0).getDate();
  }, [monthYear]);

  const maxPresentDays = daysInMonth - holidays.length;

  useEffect(() => {
    const handler = (e) => {
      if (deptDropRef.current && !deptDropRef.current.contains(e.target)) {
        setShowDeptDrop(false);
        setDeptSearchInput(
          selectedDept ? departments.find(d => d._id === selectedDept)?.name || 'All Departments' : 'All Departments'
        );
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedDept, departments]);

  const filteredDepartments = useMemo(() => {
    const q = deptSearchInput.toLowerCase();
    if (q === 'all departments' || q === '') return departments;
    return departments.filter(d => d.name.toLowerCase().includes(q));
  }, [deptSearchInput, departments]);

  const handleSelectDepartment = (dept) => {
    setSelectedDept(dept?._id || '');
    setDeptSearchInput(dept?.name || 'All Departments');
    setShowDeptDrop(false);
  };

  const displayedEmployees = useMemo(() => {
    if (!selectedDept) return employees;
    return employees.filter(e => String(e.department?._id || e.department) === selectedDept);
  }, [employees, selectedDept]);

  // Handle manual input change
  const handlePresentChange = (empId, value) => {
    if (savedRecords[empId] || attendance[empId]?.marked) return; 
    // Allow empty string
    if (value === '') {
      setAttendance(prev => ({ ...prev, [empId]: { ...prev[empId], present: '', marked: false } }));
      return;
    }
    // Allow decimals with step 0.5
    const num = parseFloat(value) || 0;
    const clamped = Math.min(Math.max(0, num), maxPresentDays);
    setAttendance(prev => ({ ...prev, [empId]: { ...prev[empId], present: clamped, marked: false } }));
  };

  // Toggle individual employee present / unmark locally
  const toggleMarkPresent = (empId) => {
    if (savedRecords[empId]) return; // Cannot edit saved DB records here
    setAttendance(prev => {
      const current = prev[empId] || {};
      const isMarked = current.marked;
      
      // If unmarking, keep the current present value
      if (isMarked) {
        return { 
          ...prev, 
          [empId]: { 
            present: current.present, 
            marked: false 
          } 
        };
      }
      
      // If marking, use current value or default to maxPresentDays
      const presentDays = (current.present !== '' && current.present !== undefined) 
        ? current.present 
        : maxPresentDays;

      return { 
        ...prev, 
        [empId]: { 
          present: presentDays, 
          marked: true 
        } 
      };
    });
  };

  const getTotalPresent = (empId) => {
    if (savedRecords[empId]) return savedRecords[empId].totalPresent;
    const present = parseFloat(attendance[empId]?.present) || 0;
    return Math.min(present + holidays.length, daysInMonth);
  };

  // Mark ALL currently displayed (un-saved) employees as present
  const handleMarkAllPresent = () => {
    const eligible = displayedEmployees.filter(e => !!(e.department?._id || e.department) && !savedRecords[e._id]);
    
    if (eligible.length === 0) {
      Swal.fire('Info', 'All displayed employees are already marked or saved.', 'info');
      return;
    }

    const newAttendance = { ...attendance };
    eligible.forEach(e => {
      newAttendance[e._id] = { 
        present: (newAttendance[e._id]?.present !== undefined) ? newAttendance[e._id].present : maxPresentDays, 
        marked: true 
      };
    });
    setAttendance(newAttendance);
  };

  // Save only locally marked employees to DB
  const handleSaveAttendance = async () => {
    const [year, month] = monthYear.split('-');
    const toSave = displayedEmployees.filter(e => {
      const hasDept = !!(e.department?._id || e.department);
      const isMarked = attendance[e._id]?.marked;
      const isAlreadySaved = !!savedRecords[e._id];
      return hasDept && isMarked && !isAlreadySaved;
    });

    if (toSave.length === 0) {
      Swal.fire('No Pending Data', 'Please mark at least one new employee as present before saving.', 'info');
      return;
    }

    setSaving(true);
    try {
      const records = toSave.map(e => ({
        employee: e._id,
        presentDays: parseFloat(attendance[e._id]?.present) || 0,
      }));
      const response = await api.post('/attendance/mark', {
        college: selectedCollege,
        month: Number(month),
        year: Number(year),
        daysInMonth,
        records,
      });
      
      Swal.fire('Saved!', `Attendance saved for ${toSave.length} employee(s).`, 'success');
      
      // Update savedRecords with the response data to show correct values immediately
      const newSavedRecords = { ...savedRecords };
      (response.data.data || []).forEach(rec => {
        const empId = rec.employee?._id || rec.employee;
        newSavedRecords[empId] = rec;
      });
      setSavedRecords(newSavedRecords);
      
      // Clear local marked state
      const cleared = { ...attendance };
      toSave.forEach(e => delete cleared[e._id]);
      setAttendance(cleared);
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="attendance-of-employee-container">
      <h2 className="attendance-of-employee-title">Employee Attendance</h2>
      <AttendanceNav />

      {/* Filters */}
      <div className="attendance-of-employee-filters-wrapper">
        {isAdmin && (
          <div className="attendance-of-employee-filter-group">
            <label className="attendance-of-employee-filter-label">College</label>
            <select className="attendance-of-employee-month-input"
              value={selectedCollege}
              onChange={e => { setSelectedCollege(e.target.value); setSelectedDept(''); setDeptSearchInput('All Departments'); setAttendance({}); }}>
              <option value="">-- Select College --</option>
              {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        )}

        <div className="attendance-of-employee-filter-group">
          <label className="attendance-of-employee-filter-label">Month & Year</label>
          <input type="month" className="attendance-of-employee-month-input"
            value={monthYear} onChange={e => { setMonthYear(e.target.value); setAttendance({}); }} />
        </div>

        <div className="attendance-of-employee-filter-group" ref={deptDropRef}>
          <label className="attendance-of-employee-filter-label">Department</label>
          <div className="attendance-of-employee-searchable-dropdown">
            <FaSearch className="attendance-of-employee-search-icon" />
            <input type="text" className="attendance-of-employee-dept-input"
              value={deptSearchInput}
              onChange={e => { setDeptSearchInput(e.target.value); setShowDeptDrop(true); }}
              onClick={() => { setDeptSearchInput(''); setShowDeptDrop(true); }}
              placeholder="Search department..."
            />
            <FaChevronDown className="attendance-of-employee-chevron-icon" />
            {showDeptDrop && (
              <div className="attendance-of-employee-dropdown-list">
                <ul>
                  <li onClick={() => handleSelectDepartment(null)}>All Departments</li>
                  {filteredDepartments.map(dept => (
                    <li key={dept._id} onClick={() => handleSelectDepartment(dept)}>{dept.name}</li>
                  ))}
                  {filteredDepartments.length === 0 && (
                    <li className="attendance-of-employee-dropdown-empty">No departments found</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>

        {selectedCollege && (
          <div className="attendance-of-employee-filter-group" style={{ justifyContent: 'flex-end' }}>
            <label className="attendance-of-employee-filter-label">Holidays This Month</label>
            <div className="attendance-holiday-badge">
              🎉 {holidays.length} Holiday{holidays.length !== 1 ? 's' : ''}
              <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: 8 }}>
                (Max present: {maxPresentDays} days)
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="attendance-of-employee-table-wrapper">
        <table className="attendance-of-employee-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th className="attendance-of-employee-align-left">Employee Name</th>
              <th className="att-col-dept">Department</th>
              <th className="att-col-days">No. of Days</th>
              <th className="att-col-holiday">Holiday</th>
              <th>Present Days</th>
              <th className="att-col-total">Total Present</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {displayedEmployees.length === 0 ? (
              <tr>
                <td colSpan="8" className="attendance-of-employee-empty-msg">
                  {selectedCollege ? 'No employees found.' : 'Please select a college.'}
                </td>
              </tr>
            ) : displayedEmployees.map((emp, i) => {
              const hasDept = !!(emp.department?._id || emp.department);
              
              const isSaved     = !!savedRecords[emp._id];
              const rec         = attendance[emp._id];
              const localMarked = rec?.marked || false;
              
              // Resolve Present Value, Holiday Count, and Total (DB vs Local Edit)
              let presentValue = '';
              let displayHolidays = holidays.length; // Always show current month holidays for unsaved
              let displayTotal = '—';
              
              if (isSaved) {
                // Show values from database
                presentValue = savedRecords[emp._id].presentDays;
                displayHolidays = savedRecords[emp._id].holidays;
                displayTotal = savedRecords[emp._id].totalPresent;
              } else {
                // Show local input value or calculate
                if (rec?.present !== undefined && rec.present !== '') {
                  presentValue = rec.present;
                  displayTotal = getTotalPresent(emp._id);
                } else if (localMarked) {
                  // Marked but no value entered yet, use maxPresentDays
                  presentValue = maxPresentDays;
                  displayTotal = Math.min(maxPresentDays + holidays.length, daysInMonth);
                }
              }
              
              // Input disabled if Saved to DB OR Marked Locally
              const isInputDisabled = isSaved || localMarked;

              return (
                <React.Fragment key={emp._id}>
                  <tr className={`attendance-of-employee-main-row ${!hasDept ? 'att-row-no-dept' : ''}`}>
                    <td>{i + 1}</td>
                    <td className="attendance-of-employee-align-left">
                      <div className="attendance-of-employee-name-wrapper"
                        onClick={() => setExpandedRow(expandedRow === emp._id ? null : emp._id)}>
                        <span>{emp.name}</span>
                        <button className="attendance-of-employee-expand-btn">
                          {expandedRow === emp._id ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                    </td>
                    <td className="att-col-dept">
                      {hasDept
                        ? <span className="attendance-of-employee-badge-dept">{emp.department?.name || '—'}</span>
                        : <span className="att-no-dept-badge">No Dept</span>
                      }
                    </td>
                    <td className="att-col-days">{daysInMonth}</td>
                    <td className="att-col-holiday">{displayHolidays}</td>
                    <td>
                      {hasDept ? (
                        <input
                          type="number"
                          className="att-present-input"
                          min="0"
                          max={maxPresentDays}
                          step="0.5"
                          value={presentValue}
                          placeholder="0"
                          disabled={isInputDisabled}
                          style={isInputDisabled ? { background: '#f1f5f9', cursor: 'not-allowed', color: '#64748b' } : {}}
                          onChange={e => handlePresentChange(emp._id, e.target.value)}
                        />
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>—</span>
                      )}
                    </td>
                    <td className="att-col-total">
                      {hasDept && presentValue !== ''
                        ? <span className="attendance-of-employee-badge-total">{displayTotal} / {daysInMonth}</span>
                        : <span className="attendance-of-employee-badge-total" style={{ background: '#f1f5f9', color: '#94a3b8', borderColor: '#e2e8f0' }}>— / {daysInMonth}</span>
                      }
                    </td>
                    <td>
                      {hasDept ? (
                        isSaved ? (
                          <span className="att-badge-present-locked">✓ Present</span>
                        ) : localMarked ? (
                          <button 
                            className="att-present-status-btn" 
                            style={{borderColor: '#e74c3c', color: '#e74c3c'}} 
                            onClick={() => toggleMarkPresent(emp._id)}
                          >
                            Unmark
                          </button>
                        ) : (
                          <button 
                            className="att-present-status-btn" 
                            onClick={() => toggleMarkPresent(emp._id)}
                          >
                            Present
                          </button>
                        )
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>—</span>
                      )}
                    </td>
                  </tr>
                  {expandedRow === emp._id && (
                    <tr className="attendance-of-employee-expanded-row">
                      <td colSpan="8">
                        <div className="attendance-of-employee-expanded-content">
                          <div className="attendance-of-employee-expanded-item att-exp-dept">
                            <span className="attendance-of-employee-exp-label">Department</span>
                            <span className="attendance-of-employee-exp-value">{emp.department?.name || '—'}</span>
                          </div>
                          <div className="attendance-of-employee-expanded-item att-exp-days">
                            <span className="attendance-of-employee-exp-label">No. of Days</span>
                            <span className="attendance-of-employee-exp-value">{daysInMonth}</span>
                          </div>
                          <div className="attendance-of-employee-expanded-item att-exp-holiday">
                            <span className="attendance-of-employee-exp-label">Holidays</span>
                            <span className="attendance-of-employee-exp-value">{displayHolidays}</span>
                          </div>
                          <div className="attendance-of-employee-expanded-item att-exp-total">
                            <span className="attendance-of-employee-exp-label">Total Present</span>
                            <span className="attendance-of-employee-exp-value">{presentValue !== '' ? `${displayTotal} / ${daysInMonth}` : '—'}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {displayedEmployees.length > 0 && (
        <div className="att-mark-present-bar">
          <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
            {displayedEmployees.filter(e => !(e.department?._id || e.department)).length > 0 && (
              <span style={{ color: '#f59e0b' }}>
                ⚠ {displayedEmployees.filter(e => !(e.department?._id || e.department)).length} employee(s) without department will be skipped.
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="att-mark-all-btn" onClick={handleMarkAllPresent} disabled={saving}>
              ✓ Mark All Present
            </button>
            <button className="att-mark-present-btn" onClick={handleSaveAttendance} disabled={saving}>
              <FaSave /> {saving ? 'Saving...' : 'Save Attendance'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;